import { useUser } from '@clerk/nextjs';
import { Button, buttonVariants } from '@web-app/components/ui/Button/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@web-app/components/ui/Dialog/dialog';
import { useAvailabilityReminder } from '@web-app/hooks/availability/useAvailabilityReminder';
import { useAvailabilityReminderCooldown } from '@web-app/hooks/availability/useAvailabilityReminderCooldown';
import { useAvailabilityReminderTracking } from '@web-app/hooks/availability/useAvailabilityReminderTracking';
import {
  availabilityReminderDescription,
  availabilityReminderTitle,
  isAvailabilityReminderRouteExcluded,
} from '@web-app/utils/availability-reminder.utils';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { AvailabilityReminderDetails } from './AvailabilityReminderDetails';

export function AvailabilityReminderModal() {
  const router = useRouter();
  const { user, isLoaded } = useUser();
  const excluded = isAvailabilityReminderRouteExcluded(router.route);
  if (!router.isReady || excluded || !isLoaded || !user) return null;

  return <EligibleAvailabilityReminderModal />;
}

function EligibleAvailabilityReminderModal() {
  const trackUpdateClick = useAvailabilityReminderTracking('modal');
  const { canShow, recordShown } = useAvailabilityReminderCooldown();
  const [isOpen, setIsOpen] = useState(false);
  const { reminder, calendarHref, isFetching, isError } =
    useAvailabilityReminder(canShow || isOpen);

  useEffect(() => {
    if (!reminder) {
      setIsOpen(false);
    } else if (canShow && !isOpen && !isFetching && !isError) {
      recordShown();
      setIsOpen(true);
    }
  }, [reminder, canShow, isOpen, isFetching, isError, recordShown]);

  const dismiss = () => {
    // Start a full cooldown after dismissal if the modal was left open for a while.
    recordShown();
    setIsOpen(false);
  };

  if (!reminder) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-2xl gap-0 overflow-y-auto p-0">
        <DialogHeader className="border-orange-200/60 bg-orange-50/60 pr-12">
          <DialogTitle>{availabilityReminderTitle(reminder)}</DialogTitle>
          <DialogDescription>
            {availabilityReminderDescription(reminder)}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <AvailabilityReminderDetails reminder={reminder} />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={dismiss}>
            Not now
          </Button>
          <Link
            href={calendarHref}
            onClick={() => {
              trackUpdateClick(reminder);
              dismiss();
            }}
            className={buttonVariants()}
          >
            Update availability
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
